// Client-side mirror of backend/src/utils/invoiceAdjustments.js for the
// deposit request shown on invoices and time billing invoices. The backend
// stores the authoritative amount in totals.depositRequested; this is used
// for live previews while editing.

// Rounds half-up to cents (see the backend helper for why not toFixed).
export function roundMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(Number((n * 100).toPrecision(15))) / 100;
}

export function computeDepositRequestAmount(depositRequest, total) {
  const value = Number(depositRequest?.value);
  const safeTotal = Math.max(roundMoney(total), 0);
  if (!Number.isFinite(value) || value <= 0) return 0;
  if (depositRequest.type === 'amount') return Math.min(roundMoney(value), safeTotal);
  return roundMoney((safeTotal * Math.min(value, 100)) / 100);
}

export function depositRequestLabel(depositRequest) {
  return depositRequest?.type === 'percent' && Number(depositRequest.value) > 0
    ? `Deposit Requested (${Number(depositRequest.value)}%)`
    : 'Deposit Requested';
}

// What's still owed toward the deposit after payments so far.
export function depositStillDue(depositRequested, paid) {
  return roundMoney(Math.max(Number(depositRequested || 0) - Number(paid || 0), 0));
}

// Invoice/due dates are stored as YYYY-MM-DD; new Date('2026-10-13') parses
// as UTC midnight and shows the previous day in US time zones, so build
// date-only values in local time instead.
export function parseInvoiceDate(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  const date = match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatInvoiceDate(value) {
  const date = parseInvoiceDate(value);
  return date ? date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '';
}

// YYYY-MM-DD in local time, for <input type="date"> values.
export function toDateInputValue(value) {
  const date = parseInvoiceDate(value);
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
